import { InvoiceConfig, UserProfile } from '../types';
import { supabase } from './supabaseClient';
import { normalizeCompanyName, sameCompany } from './companyScope';

/**
 * Die Rechnungs-Grundeinstellungen gelten fuer die FIRMA, nicht fuer den
 * einzelnen Anmeldenamen.
 *
 * Der Einstellungsdialog (AdminGlobalSettings) schreibt sie nach
 * profiles.settings.invoiceConfig — also in das Profil des Admins, der sie
 * eingetragen hat. Gelesen wurden sie bisher aus dem Profil des ANGEMELDETEN
 * Nutzers. Fuer jeden Mitarbeiter blieb der Block damit leer, und seine
 * Rechnungen entstanden mit den eingebauten Standardwerten statt mit denen
 * des Betriebs — sichtbar zuerst am Zahlungsziel.
 *
 * Mandant ist der company_name-String (wie im Rest der Anwendung).
 */

/** Rollen, deren Einstellungsblock fuer die Firma gilt, in dieser Rangfolge. */
const COMPANY_CONFIG_ROLES = ['super_admin', 'admin'] as const;

type CompanyConfigRole = (typeof COMPANY_CONFIG_ROLES)[number];

export interface CompanyConfigRow {
    id: string;
    role?: string | null;
    company_name?: string | null;
    /** Stabile Mandanten-ID aus companies.sql. Fehlt, solange #9 nicht lief. */
    company_id?: string | null;
    /** Das ganze profiles.settings-JSON; invoiceConfig ist nur einer der Bloecke. */
    settings?: ({ invoiceConfig?: Partial<InvoiceConfig> | null } & Record<string, unknown>) | null;
}

/**
 * Firmenname vergleichbar machen — dieselbe Regel wie im Rest der Anwendung
 * (companyScope). Eigene Fassung war sie bis 2026-08-24; zwei Regeln fuer
 * dieselbe Frage laufen frueher oder spaeter auseinander.
 */
export const normalizeCompany = normalizeCompanyName;

/** Ein leeres Objekt ist keine Einstellung — sonst gewaenne ein nie befuellter Block. */
const hasConfig = (row: CompanyConfigRow): boolean =>
    !!row.settings?.invoiceConfig && Object.keys(row.settings.invoiceConfig).length > 0;

const roleRank = (role?: string | null): number => {
    const idx = COMPANY_CONFIG_ROLES.indexOf(role as CompanyConfigRole);
    return idx < 0 ? COMPANY_CONFIG_ROLES.length : idx;
};

/**
 * Waehlt aus den Profilen einer Firma den massgeblichen Einstellungsblock.
 *
 * Das eigene Profil schlaegt alles: wer die Einstellungen gerade gepflegt
 * hat, muss seine eigene Aenderung sofort wirken sehen — sonst zeigte der
 * Dialog einen Wert an, mit dem der naechste Beleg nicht rechnet.
 * Darunter entscheidet die Rolle (super_admin vor admin) und zuletzt die id,
 * damit die Auswahl bei zwei gleichrangigen Admins stabil bleibt und nicht
 * von der Reihenfolge der Datenbankantwort abhaengt.
 */
export const pickCompanyInvoiceConfig = (
    rows: CompanyConfigRow[],
    self: CompanySelf,
): Partial<InvoiceConfig> | undefined =>
    pickCompanySettingsBlock(rows, self, (r) => (hasConfig(r) ? r.settings!.invoiceConfig! : undefined));

export type CompanySelf = { id?: string; company_name?: string | null; company_id?: string | null };

/**
 * Dieselbe Auswahlregel fuer JEDEN firmenweiten Einstellungsblock
 * (Rechnungen, Projekt-Kategorisierung, …): `wert` liest den Block aus einer
 * Profilzeile und liefert undefined, wenn er dort nicht gepflegt ist — nur
 * solche Zeilen sind Kandidaten. Rangfolge wie oben beschrieben: eigenes
 * Profil, dann super_admin vor admin, dann id.
 */
export const pickCompanySettingsBlock = <T>(
    rows: CompanyConfigRow[],
    self: CompanySelf,
    wert: (row: CompanyConfigRow) => T | undefined,
): T | undefined => {
    // Ohne jede Firmenangabe gibt es keinen Mandanten, dem der Block gehoeren
    // koennte. Der Riegel muss VOR sameCompany stehen: zwei leere Namen
    // vergleichen sich als gleich, ein Profil ohne Firma erbte sonst den Block
    // irgendeines anderen ohne Firma.
    const hasScope = !!(self.company_id ?? '').trim() || !!normalizeCompany(self.company_name);
    if (!hasScope) return undefined;
    const candidates = rows
        .filter((r) => sameCompany(r, self) && roleRank(r.role) < COMPANY_CONFIG_ROLES.length)
        .map((r) => ({ row: r, wert: wert(r) }))
        .filter((c) => c.wert !== undefined);
    if (!candidates.length) return undefined;
    const own = candidates.find((c) => c.row.id === self.id);
    if (own) return own.wert;
    candidates.sort((a, b) => roleRank(a.row.role) - roleRank(b.row.role) || a.row.id.localeCompare(b.row.id));
    return candidates[0].wert;
};

/**
 * Laedt den Einstellungsblock der Firma. Best effort wie resolveCustomerTerms:
 * schlaegt die Abfrage fehl oder findet sie nichts, gilt der eigene Block —
 * an einer Einstellung darf die Anmeldung nicht scheitern.
 */
/**
 * Holt die in Frage kommenden Profile.
 *
 * Zwei Anlaeufe, weil `profiles.company_id` erst mit companies.sql (#9)
 * entsteht und Client und Datenbank in beliebiger Reihenfolge ausgerollt
 * werden duerfen: fehlt die Spalte, antwortet PostgREST mit 42703, und ein
 * einziger Versuch liesse den ganzen Firmenblock ausfallen — jeder Beleg
 * entstuende wieder mit den eingebauten Standardwerten statt mit denen des
 * Betriebs. Genau davor sollte dieses Modul schuetzen.
 */
export const fetchCompanyRows = async (): Promise<CompanyConfigRow[]> => {
    const roles = [...COMPANY_CONFIG_ROLES];
    const withId = await supabase
        .from('profiles')
        .select('id, role, company_name, company_id, settings')
        .in('role', roles);
    if (!withId.error) return (withId.data ?? []) as CompanyConfigRow[];

    const legacy = await supabase
        .from('profiles')
        .select('id, role, company_name, settings')
        .in('role', roles);
    if (legacy.error) throw legacy.error;
    return (legacy.data ?? []) as CompanyConfigRow[];
};

export const loadCompanyInvoiceConfig = async (
    profile: UserProfile | null | undefined,
): Promise<Partial<InvoiceConfig> | undefined> => {
    const ownConfig = (profile as any)?.settings?.invoiceConfig as
        | Partial<InvoiceConfig>
        | undefined;
    // Die ID reicht als Mandantenmerkmal — ein Profil mit company_id, aber
    // ohne getippten Firmennamen ist kein Grund mehr, den Block der Firma zu
    // uebergehen.
    if (!profile?.company_name && !(profile as any)?.company_id) return ownConfig;
    try {
        const picked = pickCompanyInvoiceConfig(await fetchCompanyRows(), profile);
        if (picked) return picked;
    } catch (e) {
        console.warn('Firmen-Rechnungseinstellungen nicht ladbar', e);
    }
    return ownConfig;
};
